/**
 * Student Course Page
 * D2L-style structure: Content (table of contents + detail pane) and Quizzes
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import FilePreviewModal from '../../components/shared/FilePreviewModal';
import { analyzeLink } from '../../utils/embedProviders';
import Button from '../../components/shared/Button';
import Mascot from '../../components/shared/Mascot';
import UpNextCard from '../../components/shared/UpNextCard';
import { getCourseVisual } from '../../utils/courseVisuals';
import SubjectGlyph from '../../components/courses/SubjectGlyph';
import {
  getCourse,
  getCourseSlides,
  getCourseRecommendedVideos,
  getCourseQuizzes,
  getQuiz,
  submitQuiz,
  extractLectureTopics,
  CourseResponse,
  LectureResponse,
  SlideResponse,
  RecommendedVideoResponse,
  QuizListItem,
  QuizResponse,
  UpcomingQuizItem,
  getContinueWatching,
  ContinueWatchingItem,
  listContentFolders,
  listCourseFiles,
  ContentFolder,
  CourseFile,
  getMyGrades,
  StudentGrades,
  StudentGradeItem,
  listAssignments,
  getMySubmission,
  submitAssignment,
  AssignmentResponse,
  AssignmentSubmission,
  getUpcomingQuizzes,
} from '../../api/courses';
import { getStaticBaseUrl } from '../../api/client';


type TabId = 'home' | 'content' | 'quizzes' | 'assignments' | 'grades';

/** Grade row shape including the grade-release workflow fields returned by the API. */
type GradeItem = StudentGradeItem & {
  pending?: boolean;
  feedback?: string;
  item_type?: 'quiz' | 'assignment';
};

/** Status of my submission on an assignment, derived client-side. */
type AssignmentStatus = 'none' | 'submitted' | 'late' | 'graded';

const getSubmissionStatus = (sub: AssignmentSubmission | null | undefined): AssignmentStatus => {
  if (!sub) return 'none';
  if (sub.released) return 'graded';
  if (sub.submitted_late) return 'late';
  return 'submitted';
};

/** What the right pane of the Content tab is showing. */
type ContentView = 'lecture' | 'slides' | 'recommended' | 'folder' | 'files';

/** Broad file-type groups used to pick a glyph for course files. */
type FileGlyphGroup = 'archive' | 'pdf' | 'doc' | 'sheet' | 'image' | 'video' | 'other';

const getFileGlyphGroup = (fileName: string): FileGlyphGroup => {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) return 'archive';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx', 'txt', 'rtf', 'odt', 'md', 'pages'].includes(ext)) return 'doc';
  if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext)) return 'sheet';
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'heic'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext)) return 'video';
  return 'other';
};

/** Small outline glyph per file-type group (inherits currentColor). */
const FILE_GLYPHS: Record<FileGlyphGroup, React.ReactNode> = {
  archive: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 7H4a1 1 0 00-1 1v11a2 2 0 002 2h14a2 2 0 002-2V8a1 1 0 00-1-1z" />
      <path d="M3 7l1.5-3.2A1 1 0 015.4 3h13.2a1 1 0 01.9.8L21 7" />
      <path d="M10 11h4" />
    </svg>
  ),
  pdf: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      <path d="M13 3v5a2 2 0 002 2h5" />
      <path d="M9 15h6M9 18h4" />
    </svg>
  ),
  doc: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      <path d="M13 3v5a2 2 0 002 2h5" />
      <path d="M9 13h6M9 17h6" />
    </svg>
  ),
  sheet: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5z" />
      <path d="M5 10h14M5 15h14M12 10v11" />
    </svg>
  ),
  image: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  video: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  ),
  other: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      <path d="M13 3v5a2 2 0 002 2h5" />
    </svg>
  ),
};

/** Bloom's taxonomy fixed palette. White text is correct on these deep colors. */
const BLOOM_COLORS: Record<string, string> = {
  remember: '#1A2744',
  understand: '#1A5A8A',
  apply: '#1A7A3E',
  analyze: '#8B7A0E',
  evaluate: '#9C3A10',
  create: '#8B1A1A',
};

/** Renders a Bloom level badge when the question carries a Bloom label. */
const BloomBadge: React.FC<{ label?: string }> = ({ label }) => {
  if (!label) return null;
  const color = BLOOM_COLORS[label.toLowerCase()];
  if (!color) return null;
  const pretty = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  return (
    <span
      className="inline-flex flex-shrink-0 items-center rounded-full px-3 py-1 text-xs font-bold text-white"
      style={{ backgroundColor: color }}
    >
      {pretty}
    </span>
  );
};

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  {
    id: 'home',
    label: 'Home',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    id: 'content',
    label: 'Content',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    id: 'quizzes',
    label: 'Quizzes',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 'assignments',
    label: 'Assignments',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
      </svg>
    ),
  },
  {
    id: 'grades',
    label: 'Grades',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

/** Score band colors shared by the average bar and per-quiz pills. */
const getGradeBand = (pct: number): 'emerald' | 'amber' | 'red' =>
  pct >= 70 ? 'emerald' : pct >= 40 ? 'amber' : 'red';

const GRADE_BAR_CLASSES: Record<'emerald' | 'amber' | 'red', string> = {
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const GRADE_PILL_CLASSES: Record<'emerald' | 'amber' | 'red', string> = {
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300',
  red: 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300',
};

const GRADE_DOT_CLASSES: Record<'emerald' | 'amber' | 'red', string> = {
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

/** Client-side schedule state derived from available_from/due_at. */
type QuizScheduleState = 'none' | 'scheduled' | 'open' | 'closed';

const getQuizScheduleState = (quiz: Pick<QuizListItem, 'available_from' | 'due_at'>): QuizScheduleState => {
  const now = Date.now();
  const opensAt = quiz.available_from ? new Date(quiz.available_from).getTime() : null;
  const dueAt = quiz.due_at ? new Date(quiz.due_at).getTime() : null;
  if (opensAt !== null && opensAt > now) return 'scheduled';
  if (dueAt !== null && dueAt < now) return 'closed';
  if (dueAt !== null) return 'open';
  return 'none';
};

/** "Jul 12, 3:00 PM" style short local date + time. */
const formatShortDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

/** "Jul 12" style short local date. */
const formatShortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const StudentCoursePage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  const [course, setCourse] = useState<CourseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabId>('home');
  const [lastWatched, setLastWatched] = useState<ContinueWatchingItem | null>(null);
  const [courseUpNext, setCourseUpNext] = useState<UpcomingQuizItem | null>(null);

  // Nearest outstanding deadline in this course (completed items excluded server-side)
  useEffect(() => {
    if (!courseId) return;
    getUpcomingQuizzes()
      .then((items) => setCourseUpNext(items.find((i) => i.course_id === courseId) || null))
      .catch(() => {});
  }, [courseId, activeTab]);

  // Content tab: table of contents + right pane selection
  const [contentView, setContentView] = useState<ContentView>('lecture');
  const [selectedLectureId, setSelectedLectureId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [tocLecturesOpen, setTocLecturesOpen] = useState(true);
  const [tocMaterialsOpen, setTocMaterialsOpen] = useState(true);
  // Once the student picks something themselves, stop auto-selecting
  const manualSelectRef = useRef(false);

  // Most recently watched, unfinished lecture in this course (for the hero card)
  useEffect(() => {
    if (!courseId) return;
    getContinueWatching()
      .then((items) => setLastWatched(items.find((i) => i.course_id === courseId) || null))
      .catch(() => {});
  }, [courseId]);

  // Slides state
  const [slides, setSlides] = useState<SlideResponse[]>([]);
  const [slidesLoading, setSlidesLoading] = useState(false);

  // Recommended Videos state
  const [recommendedVideos, setRecommendedVideos] = useState<RecommendedVideoResponse[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);

  // Content folders + course files state (D2L-style materials, read-only)
  const [contentFolders, setContentFolders] = useState<ContentFolder[]>([]);
  const [previewFile, setPreviewFile] = useState<CourseFile | null>(null);
  const [selectedEmbedLink, setSelectedEmbedLink] = useState<CourseFile | null>(null);
  const [courseFiles, setCourseFiles] = useState<CourseFile[]>([]);
  const [courseFilesLoading, setCourseFilesLoading] = useState(false);

  // Quizzes state
  const [quizzes, setQuizzes] = useState<QuizListItem[]>([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  type QuizView = 'list' | 'taking' | 'submitted';
  const [quizView, setQuizView] = useState<QuizView>('list');
  const [activeQuiz, setActiveQuiz] = useState<QuizResponse | null>(null);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizLoadingId, setQuizLoadingId] = useState<string | null>(null);
  // Stepper navigation for the quiz-taking view (presentation state only)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Grades state
  const [grades, setGrades] = useState<StudentGrades | null>(null);
  const [gradesLoading, setGradesLoading] = useState(false);
  // Which grade rows have their instructor feedback expanded
  const [expandedFeedback, setExpandedFeedback] = useState<Record<string, boolean>>({});

  // Assignments state
  const [assignments, setAssignments] = useState<AssignmentResponse[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [mySubmissions, setMySubmissions] = useState<Record<string, AssignmentSubmission | null>>({});
  type AssignmentView = 'list' | 'detail';
  const [assignmentView, setAssignmentView] = useState<AssignmentView>('list');
  const [activeAssignmentId, setActiveAssignmentId] = useState<string | null>(null);
  // Submit form state (shared between first submission and resubmission)
  const [submitFiles, setSubmitFiles] = useState<File[]>([]);
  const [submitComment, setSubmitComment] = useState('');
  const [assignmentSubmitting, setAssignmentSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showResubmitForm, setShowResubmitForm] = useState(false);


  // Load course
  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  // Load slides and recommended videos when the Content tab is active.
  // Both feed the Table of Contents counts and the right pane.
  useEffect(() => {
    if (activeTab === 'content' && courseId) {
      loadSlides();
    }
  }, [activeTab, courseId]);

  useEffect(() => {
    if (activeTab === 'content' && courseId) {
      loadRecommendedVideos();
    }
  }, [activeTab, courseId]);

  useEffect(() => {
    if (activeTab === 'content' && courseId) {
      loadFoldersAndFiles();
    }
  }, [activeTab, courseId]);

  // Load quizzes
  useEffect(() => {
    if (activeTab === 'quizzes' && courseId) {
      loadQuizzes();
      setQuizView('list');
    }
  }, [activeTab, courseId]);

  // Load assignments (and my submissions) lazily when the Assignments tab activates
  useEffect(() => {
    if (activeTab === 'assignments' && courseId) {
      loadAssignments();
      setAssignmentView('list');
    }
  }, [activeTab, courseId]);

  // Load grades when the Grades tab activates
  useEffect(() => {
    if (activeTab === 'grades' && courseId) {
      loadGrades();
    }
  }, [activeTab, courseId]);

  // Auto-select the last-watched lecture in the ToC, falling back to the
  // first lecture. Stops as soon as the student makes a manual selection.
  useEffect(() => {
    if (!course || course.lectures.length === 0 || manualSelectRef.current) return;
    const sorted = [...course.lectures].sort((a, b) => a.order - b.order);
    const lw = lastWatched ? sorted.find((l) => l.lecture_id === lastWatched.lecture_id) : undefined;
    setSelectedLectureId((lw || sorted[0]).lecture_id);
  }, [course, lastWatched]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getCourse(courseId!);
      setCourse(data);
    } catch (err: any) {
      setError('Failed to load course');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSlides = async () => {
    try {
      setSlidesLoading(true);
      const data = await getCourseSlides(courseId!);
      setSlides(data);
    } catch (err: any) {
      console.error('Failed to load slides:', err);
      setSlides([]);
    } finally {
      setSlidesLoading(false);
    }
  };

  const loadRecommendedVideos = async () => {
    try {
      setRecommendedLoading(true);
      const data = await getCourseRecommendedVideos(courseId!);
      setRecommendedVideos(data);
    } catch (err: any) {
      console.error('Failed to load recommended videos:', err);
      setRecommendedVideos([]);
    } finally {
      setRecommendedLoading(false);
    }
  };

  const loadFoldersAndFiles = async () => {
    try {
      setCourseFilesLoading(true);
      const [folders, files] = await Promise.all([
        listContentFolders(courseId!),
        listCourseFiles(courseId!),
      ]);
      setContentFolders(folders);
      setCourseFiles(files);
    } catch (err: any) {
      console.error('Failed to load course files:', err);
      setContentFolders([]);
      setCourseFiles([]);
    } finally {
      setCourseFilesLoading(false);
    }
  };

  const loadQuizzes = async () => {
    try {
      setQuizzesLoading(true);
      const data = await getCourseQuizzes(courseId!);
      setQuizzes(data);
    } catch (err: any) {
      console.error('Failed to load quizzes:', err);
      setQuizzes([]);
    } finally {
      setQuizzesLoading(false);
    }
  };

  const loadGrades = async () => {
    try {
      setGradesLoading(true);
      const data = await getMyGrades(courseId!);
      setGrades(data);
    } catch (err: any) {
      console.error('Failed to load grades:', err);
      setGrades({ items: [], average: null });
    } finally {
      setGradesLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      setAssignmentsLoading(true);
      const data = await listAssignments(courseId!);
      setAssignments(data);
      const subs = await Promise.all(
        data.map((a) => getMySubmission(a.assignment_id).catch(() => null))
      );
      const map: Record<string, AssignmentSubmission | null> = {};
      data.forEach((a, i) => { map[a.assignment_id] = subs[i]; });
      setMySubmissions(map);
    } catch (err: any) {
      console.error('Failed to load assignments:', err);
      setAssignments([]);
      setMySubmissions({});
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const openAssignment = (assignmentId: string) => {
    setActiveAssignmentId(assignmentId);
    setSubmitFiles([]);
    setSubmitComment('');
    setUploadProgress(null);
    setShowResubmitForm(false);
    setAssignmentView('detail');
  };

  const handleSubmitAssignment = async (assignmentId: string) => {
    if (submitFiles.length === 0 || assignmentSubmitting) return;
    setAssignmentSubmitting(true);
    setUploadProgress(0);
    try {
      await submitAssignment(assignmentId, submitFiles, submitComment.trim() || undefined, (e: any) => {
        if (e?.total) setUploadProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      });
      const sub = await getMySubmission(assignmentId).catch(() => null);
      setMySubmissions((prev) => ({ ...prev, [assignmentId]: sub }));
      setSubmitFiles([]);
      setSubmitComment('');
      setShowResubmitForm(false);
    } catch (err: any) {
      alert('Failed to submit assignment');
    } finally {
      setAssignmentSubmitting(false);
      setUploadProgress(null);
    }
  };

  const startQuiz = async (quizId: string) => {
    // Guard: scheduled (not yet open) or closed (past due) quizzes cannot be started
    const listItem = quizzes.find((q) => q.quiz_id === quizId);
    if (listItem) {
      const state = getQuizScheduleState(listItem);
      if (state === 'scheduled' || state === 'closed') return;
    }
    try {
      setQuizLoadingId(quizId);
      const quiz = await getQuiz(quizId);
      setActiveQuiz(quiz);
      setQuizAnswers({});
      setCurrentQuestionIndex(0);
      setQuizView('taking');
    } catch (err: any) {
      alert('Failed to load quiz');
    } finally {
      setQuizLoadingId(null);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!activeQuiz) return;
    setQuizSubmitting(true);
    try {
      // Grades are no longer revealed at submission time; the instructor
      // reviews the attempt and releases the grade to the Grades tab.
      await submitQuiz(activeQuiz.quiz_id, quizAnswers);
      setQuizView('submitted');
    } catch (err: any) {
      alert('Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  // Extract topics for a lecture on demand (kept available for lecture detail)
  const handleExtractTopics = async (lectureId: string) => {
    try {
      await extractLectureTopics(lectureId);
      await loadCourse();
    } catch (err: any) {
      console.error('Failed to extract topics:', err);
    }
  };

  // ToC selection helpers
  const selectLecture = (lectureId: string) => {
    manualSelectRef.current = true;
    setSelectedLectureId(lectureId);
    setContentView('lecture');
  };

  const selectMaterials = (view: 'slides' | 'recommended') => {
    manualSelectRef.current = true;
    setContentView(view);
  };

  const selectFolder = (folderId: string) => {
    manualSelectRef.current = true;
    setSelectedFolderId(folderId);
    setContentView('folder');
  };

  const selectRootFiles = () => {
    manualSelectRef.current = true;
    setSelectedFolderId(null);
    setContentView('files');
  };


  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-8">
          <div className="h-44 animate-pulse rounded-3xl bg-gray-200 dark:bg-secondary-800" />
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl bg-gray-200 dark:bg-secondary-800" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !course) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
          <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-red-50 text-red-500 dark:bg-red-900/30 dark:text-red-400">
            <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
            </svg>
          </div>
          <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">{error || 'Course not found'}</h3>
          <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
            We couldn't load this course. Head back and try again.
          </p>
          <Button onClick={() => navigate('/dashboard/student')} className="mt-6">
            Back to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  const visual = getCourseVisual(course.title);
  const sortedLectures = [...course.lectures].sort((a, b) => a.order - b.order);
  const selectedIdx = sortedLectures.findIndex((l) => l.lecture_id === selectedLectureId);
  const selectedLecture = selectedIdx >= 0 ? sortedLectures[selectedIdx] : null;
  const apiUrl = getStaticBaseUrl();

  // Recommended videos tied to the selected lecture (when the API provides
  // a lecture_id on the video; course-wide videos stay under Course materials).
  const lectureRecommendedVideos = selectedLecture
    ? recommendedVideos.filter(
        (v) => (v as RecommendedVideoResponse & { lecture_id?: string }).lecture_id === selectedLecture.lecture_id
      )
    : [];

  /** Shared recommended-video card (used in the materials view and per-lecture). */
  const renderVideoCard = (video: RecommendedVideoResponse) => (
    <a
      key={video.video_id}
      href={video.youtube_url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative flex cursor-pointer flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden rounded-xl">
        <img
          src={`https://img.youtube.com/vi/${video.youtube_video_id}/hqdefault.jpg`}
          alt={video.title}
          className="h-44 w-full object-cover transition-transform duration-200 group-hover:scale-105"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-all duration-200 group-hover:bg-black/20">
          <div className="scale-75 rounded-full bg-red-600 p-3 opacity-0 shadow-lg transition-all duration-200 group-hover:scale-100 group-hover:opacity-100">
            <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
      </div>

      {/* Icon tile + title row */}
      <div className="mt-4 flex items-start gap-3">
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </span>
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-base font-bold leading-snug text-gray-900 dark:text-white">{video.title}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">YouTube video</p>
        </div>
      </div>

      {/* Meta row */}
      <div className="mt-3 flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Added {new Date(video.added_at).toLocaleDateString()}
        </span>
      </div>
    </a>
  );

  /** Table of Contents entries reused by the sidebar and the mobile select. */
  const materialsEntries: { view: 'slides' | 'recommended'; label: string; count: number }[] = [
    { view: 'slides', label: 'Slides and presentations', count: slides.length },
    { view: 'recommended', label: 'Recommended videos', count: recommendedVideos.length },
  ];

  // Content folders + files (D2L-style). Empty folders are hidden from students.
  const filesInFolder = (folderId: string | null) =>
    courseFiles.filter((f) => (f.folder_id ?? null) === folderId);
  const rootFiles = filesInFolder(null);
  const visibleFolders = contentFolders.filter(
    (f) => f.file_count > 0 || filesInFolder(f.folder_id).length > 0
  );
  const selectedFolder = selectedFolderId
    ? visibleFolders.find((f) => f.folder_id === selectedFolderId) || null
    : null;

  /** Shared file row used by the folder and root Files views. */
  const renderFileRow = (file: CourseFile) => {
    const isLink = file.kind === 'link';
    const linkInfo = isLink && file.url ? analyzeLink(file.url) : null;
    return (
    <div
      key={file.file_id}
      className="flex items-center gap-4 px-5 py-4"
    >
      <span className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${isLink ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' : 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'}`}>
        {isLink ? (
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        ) : FILE_GLYPHS[getFileGlyphGroup(file.file_name)]}
      </span>
      <button
        onClick={() => {
          if (isLink && linkInfo?.embedUrl) setSelectedEmbedLink(file);
          else if (isLink && file.url) window.open(file.url, '_blank', 'noopener');
          else setPreviewFile(file);
        }}
        className="min-w-0 flex-1 text-left focus:outline-none"
        title={isLink ? 'Open this link' : 'Preview this file'}
      >
        <p className="truncate text-sm font-bold text-gray-900 transition-colors hover:text-primary-700 dark:text-white dark:hover:text-primary-300">{file.title}</p>
        <p className="mt-0.5 truncate text-xs font-medium text-gray-500 dark:text-gray-400">
          {isLink
            ? <>{linkInfo?.provider || 'Link'} &middot; {file.url}</>
            : <>{file.file_name} &middot; {formatFileSize(file.file_size)} &middot; {new Date(file.uploaded_at).toLocaleDateString()}</>}
        </p>
      </button>
      <button
        onClick={() => {
          if (isLink && linkInfo?.embedUrl) setSelectedEmbedLink(file);
          else if (isLink && file.url) window.open(file.url, '_blank', 'noopener');
          else setPreviewFile(file);
        }}
        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-2 text-xs font-bold text-gray-600 transition-colors hover:border-primary-300 hover:text-primary-700 dark:border-secondary-600 dark:text-gray-300"
      >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><circle cx="12" cy="12" r="3" />
        </svg>
        View
      </button>
      <a
        href={isLink ? (file.url || '#') : `${apiUrl}${file.file_path}`}
        {...(isLink ? {} : { download: file.file_name })}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
      >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          {isLink
            ? <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            : <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />}
        </svg>
        {isLink ? 'Open' : 'Download'}
      </a>
    </div>
    );
  };

  /** Shared folder/files detail pane (header + file rows or a quiet empty hint). */
  const renderFilesPane = (title: string, paneFiles: CourseFile[]) => (
    selectedEmbedLink && selectedEmbedLink.url ? (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3.5 dark:border-secondary-700">
          <button
            onClick={() => setSelectedEmbedLink(null)}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border-2 border-gray-200 px-3.5 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700 dark:hover:text-white"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h3 className="min-w-0 flex-1 truncate text-base font-extrabold tracking-tight text-gray-900 dark:text-white">
            {selectedEmbedLink.title}
          </h3>
          <a
            href={selectedEmbedLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
          >
            Open in new tab
          </a>
        </div>
        <iframe
          src={analyzeLink(selectedEmbedLink.url).embedUrl || selectedEmbedLink.url}
          title={selectedEmbedLink.title}
          sandbox="allow-scripts allow-same-origin allow-forms allow-presentation allow-popups"
          className="h-[75vh] w-full bg-white"
        />
      </div>
    ) :
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
      <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4 dark:border-secondary-700">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-base font-extrabold tracking-tight text-gray-900 dark:text-white">{title}</h2>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {paneFiles.length} file{paneFiles.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      {courseFilesLoading ? (
        <div className="space-y-3 p-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-200 dark:bg-secondary-700" />
          ))}
        </div>
      ) : paneFiles.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Nothing in here yet
        </p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-secondary-700">
          {paneFiles.map((file) => renderFileRow(file))}
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/dashboard/student')}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-800"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>

          {/* Course header */}
          <div className="mt-5 flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 sm:h-16 sm:w-16">
              <SubjectGlyph glyph={visual.glyph} strokeWidth={2} className="h-7 w-7 sm:h-8 sm:w-8" />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
                {course.title}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {visual.label} &middot; {course.instructor_name || course.instructor} &middot; {course.lecture_count} lectures
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="inline-flex max-w-full flex-wrap gap-1 rounded-full bg-gray-100 p-1 dark:bg-secondary-800">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-white text-primary-700 shadow-sm dark:bg-secondary-700 dark:text-white'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.id === 'content' && (
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                  activeTab === 'content' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'bg-gray-200 text-gray-500 dark:bg-secondary-600 dark:text-gray-300'
                }`}>
                  {course.lectures.length}
                </span>
              )}
              {tab.id === 'quizzes' && quizzes.length > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                  activeTab === 'quizzes' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'bg-gray-200 text-gray-500 dark:bg-secondary-600 dark:text-gray-300'
                }`}>
                  {quizzes.length}
                </span>
              )}
              {tab.id === 'assignments' && assignments.length > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                  activeTab === 'assignments' ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'bg-gray-200 text-gray-500 dark:bg-secondary-600 dark:text-gray-300'
                }`}>
                  {assignments.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in" key={activeTab}>
          {/* === HOME TAB === */}
          {activeTab === 'home' && (() => {
            const hasHomeDetails = [
              course.welcome_message,
              course.class_schedule,
              course.class_meeting_link,
              course.office_hours,
              course.office_hours_link,
              course.contact_email,
            ].some((v) => v && v.trim().length > 0);

            // Full-empty state: instructor hasn't filled in any home fields
            if (!hasHomeDetails) {
              return (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="folder" chip="#93c5fd" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
                  <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No course details yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                    The instructor hasn't added course details yet. Head over to Content to start learning.
                  </p>
                  <button
                    onClick={() => setActiveTab('content')}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Go to Content
                  </button>
                </div>
              );
            }

            const lastWatchedLecture = lastWatched
              ? sortedLectures.find((l) => l.lecture_id === lastWatched.lecture_id)
              : undefined;
            const glanceTerm = [course.semester, course.year].filter(Boolean).join(' ');

            return (
              <div className="space-y-6">
                {/* Welcome card */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">About this course</p>
                  {course.welcome_message || course.description ? (
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {course.welcome_message || course.description}
                    </p>
                  ) : (
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No welcome message posted yet</p>
                  )}
                </div>

                {/* Class meetings + Office hours */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Class meetings */}
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </span>
                      <h3 className="text-base font-extrabold tracking-tight text-gray-900 dark:text-white">Class meetings</h3>
                    </div>
                    {course.class_schedule ? (
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">{course.class_schedule}</p>
                    ) : (
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No class schedule posted yet</p>
                    )}
                    {course.class_meeting_link && (
                      <div className="mt-4">
                        <a
                          href={course.class_meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Join class
                        </a>
                        <p className="mt-2 break-all text-xs text-gray-500 dark:text-gray-400">{course.class_meeting_link}</p>
                      </div>
                    )}
                  </div>

                  {/* Office hours */}
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <h3 className="text-base font-extrabold tracking-tight text-gray-900 dark:text-white">Office hours</h3>
                    </div>
                    {course.office_hours ? (
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">{course.office_hours}</p>
                    ) : (
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No office hours posted yet</p>
                    )}
                    {course.office_hours_link && (
                      <div className="mt-4">
                        <a
                          href={course.office_hours_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Join office hours
                        </a>
                        <p className="mt-2 break-all text-xs text-gray-500 dark:text-gray-400">{course.office_hours_link}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <h3 className="text-base font-extrabold tracking-tight text-gray-900 dark:text-white">Contact</h3>
                  </div>
                  <div className="mt-3 space-y-1">
                    {(course.instructor_name || course.instructor) && (
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{course.instructor_name || course.instructor}</p>
                    )}
                    {course.contact_email ? (
                      <a
                        href={`mailto:${course.contact_email}`}
                        className="inline-block text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                      >
                        {course.contact_email}
                      </a>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No contact email posted yet</p>
                    )}
                  </div>
                </div>

                {/* Course at a glance */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-gray-200 bg-white px-6 py-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Course at a glance</p>
                  {glanceTerm && (
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{glanceTerm}</span>
                  )}
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {course.lecture_count} lecture{course.lecture_count !== 1 ? 's' : ''}
                  </span>
                  {lastWatchedLecture && (
                    <button
                      onClick={() => { selectLecture(lastWatchedLecture.lecture_id); setActiveTab('content'); }}
                      className="text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                    >
                      Pick up where you left off
                    </button>
                  )}
                </div>

                {/* Jump back in: compact resume row */}
                {lastWatched && lastWatchedLecture && (
                  <div
                    onClick={() => navigate(`/student/courses/${course.course_id}/lectures/${lastWatchedLecture.lecture_id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/student/courses/${course.course_id}/lectures/${lastWatchedLecture.lecture_id}`); } }}
                    className="group flex cursor-pointer items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
                  >
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400">Jump back in</p>
                      <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{lastWatchedLecture.title}</p>
                      <span className="mt-1.5 block h-1 w-full max-w-xs overflow-hidden rounded-full bg-gray-200 dark:bg-secondary-600">
                        <span
                          className="block h-full rounded-full bg-primary-500"
                          style={{ width: `${Math.min(100, Math.max(2, lastWatched.percent))}%` }}
                        />
                      </span>
                    </div>
                    <span className="hidden flex-shrink-0 items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all group-hover:bg-primary-700 group-active:translate-y-0.5 group-active:shadow-none sm:inline-flex">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      Resume
                    </span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* === CONTENT TAB === */}
          {activeTab === 'content' && (
            <>
              {course.lectures.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="clock" chip="#fde047" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
                  <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No lectures yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">The instructor hasn't added any lectures to this course yet</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Continue watching hero: last unfinished lecture in this course */}
                  {(() => {
                    if (!lastWatched) return null;
                    const heroIdx = sortedLectures.findIndex((l) => l.lecture_id === lastWatched.lecture_id);
                    if (heroIdx === -1) return null;
                    const hero = sortedLectures[heroIdx];
                    const fmtClock = (s: number) => {
                      const m = Math.floor(s / 60);
                      const sec = Math.floor(s % 60);
                      return `${m}:${sec.toString().padStart(2, '0')}`;
                    };
                    return (
                      <div className="space-y-3">
                        <h2 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
                          Continue watching
                        </h2>
                        <div
                          onClick={() => navigate(`/student/courses/${course.course_id}/lectures/${hero.lecture_id}`)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/student/courses/${course.course_id}/lectures/${hero.lecture_id}`); } }}
                          className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800 lg:flex-row"
                        >
                          {/* Thumbnail with time + progress */}
                          <div className="relative aspect-video flex-shrink-0 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-secondary-700 dark:to-secondary-800 lg:w-[46%]">
                            {hero.thumbnail_path ? (
                              <img
                                src={`${apiUrl}${hero.thumbnail_path}`}
                                alt={hero.title}
                                className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                            {lastWatched.duration_seconds > 0 && (
                              <span className="absolute bottom-2 right-2 rounded-md bg-black/75 px-2 py-0.5 text-xs font-bold text-white">
                                {fmtClock(lastWatched.position_seconds)} / {fmtClock(lastWatched.duration_seconds)}
                              </span>
                            )}
                            <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/20">
                              <div
                                className="h-full bg-primary-500"
                                style={{ width: `${Math.min(100, Math.max(2, lastWatched.percent))}%` }}
                              />
                            </div>
                          </div>

                          {/* Details */}
                          <div className="flex flex-1 flex-col justify-center gap-3 p-6">
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400">
                              Lecture {heroIdx + 1}
                              {hero.duration ? ` · ${formatDuration(hero.duration)}` : ''}
                            </p>
                            <h3 className="text-xl font-extrabold leading-snug tracking-tight text-gray-900 dark:text-white sm:text-2xl">
                              {hero.title}
                            </h3>
                            {(hero.summary || hero.description) && (
                              <p className="line-clamp-3 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                                {hero.summary || hero.description}
                              </p>
                            )}
                            {hero.topics && hero.topics.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {hero.topics.slice(0, 3).map((t) => (
                                  <span
                                    key={t}
                                    className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                            )}
                            <span className="mt-1 inline-flex w-fit items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all group-hover:bg-primary-700 group-active:translate-y-0.5 group-active:shadow-none">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                              Resume lecture
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Mobile Table of Contents: compact select below lg */}
                  <div className="lg:hidden">
                    <label htmlFor="toc-select" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Table of Contents
                    </label>
                    <select
                      id="toc-select"
                      value={
                        contentView === 'lecture'
                          ? `lecture:${selectedLectureId ?? ''}`
                          : contentView === 'folder'
                            ? `folder:${selectedFolderId ?? ''}`
                            : contentView
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.startsWith('lecture:')) {
                          selectLecture(value.slice('lecture:'.length));
                        } else if (value.startsWith('folder:')) {
                          selectFolder(value.slice('folder:'.length));
                        } else if (value === 'files') {
                          selectRootFiles();
                        } else {
                          selectMaterials(value as 'slides' | 'recommended');
                        }
                      }}
                      className="w-full rounded-full border-2 border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-800 dark:text-gray-200"
                    >
                      <optgroup label="Lectures">
                        {sortedLectures.map((lecture, index) => (
                          <option key={lecture.lecture_id} value={`lecture:${lecture.lecture_id}`}>
                            {index + 1}. {lecture.title}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Course materials">
                        {visibleFolders.map((folder) => (
                          <option key={folder.folder_id} value={`folder:${folder.folder_id}`}>
                            {folder.name} ({filesInFolder(folder.folder_id).length || folder.file_count})
                          </option>
                        ))}
                        {rootFiles.length > 0 && (
                          <option value="files">Files ({rootFiles.length})</option>
                        )}
                        {materialsEntries.map((entry) => (
                          <option key={entry.view} value={entry.view}>
                            {entry.label} ({entry.count})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Two-pane layout: Table of Contents + detail pane */}
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                    {/* Left sidebar: Table of Contents (lg and up) */}
                    <aside className="hidden w-64 flex-shrink-0 lg:block">
                      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                        <div className="border-b border-gray-100 px-4 py-3.5 dark:border-secondary-700">
                          <h2 className="text-sm font-extrabold tracking-tight text-gray-900 dark:text-white">
                            Table of Contents
                          </h2>
                        </div>

                        {/* Lectures module */}
                        <div className="border-b border-gray-100 dark:border-secondary-700">
                          <button
                            onClick={() => setTocLecturesOpen((v) => !v)}
                            className="flex w-full items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-500 transition-colors hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-secondary-700"
                          >
                            <span className="inline-flex items-center gap-2">
                              Lectures
                              <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold normal-case text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                {sortedLectures.length}
                              </span>
                            </span>
                            <svg
                              className={`h-4 w-4 transition-transform duration-200 ${tocLecturesOpen ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
                            >
                              <path d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {tocLecturesOpen && (
                            <div className="pb-2">
                              {sortedLectures.map((lecture, index) => {
                                const isSelected = contentView === 'lecture' && lecture.lecture_id === selectedLectureId;
                                const isLastWatched = lastWatched?.lecture_id === lecture.lecture_id;
                                return (
                                  <button
                                    key={lecture.lecture_id}
                                    onClick={() => selectLecture(lecture.lecture_id)}
                                    className={`flex w-full items-center gap-2.5 border-l-2 px-4 py-2.5 text-left text-sm transition-colors ${
                                      isSelected
                                        ? 'border-l-primary-600 bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                        : 'border-l-transparent font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700'
                                    }`}
                                  >
                                    <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold ${
                                      isSelected
                                        ? 'bg-primary-600 text-gray-900'
                                        : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                                    }`}>
                                      {index + 1}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate">{lecture.title}</span>
                                      {isLastWatched && (
                                        <span className="mt-1 block h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-secondary-600">
                                          <span
                                            className="block h-full rounded-full bg-primary-500"
                                            style={{ width: `${Math.min(100, Math.max(2, lastWatched.percent))}%` }}
                                          />
                                        </span>
                                      )}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Course materials module */}
                        <div>
                          <button
                            onClick={() => setTocMaterialsOpen((v) => !v)}
                            className="flex w-full items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-500 transition-colors hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-secondary-700"
                          >
                            Course materials
                            <svg
                              className={`h-4 w-4 transition-transform duration-200 ${tocMaterialsOpen ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
                            >
                              <path d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {tocMaterialsOpen && (
                            <div className="pb-2">
                              {visibleFolders.map((folder) => {
                                const isSelected = contentView === 'folder' && selectedFolderId === folder.folder_id;
                                const count = filesInFolder(folder.folder_id).length || folder.file_count;
                                return (
                                  <button
                                    key={folder.folder_id}
                                    onClick={() => selectFolder(folder.folder_id)}
                                    className={`flex w-full items-center gap-2.5 border-l-2 px-4 py-2.5 text-left text-sm transition-colors ${
                                      isSelected
                                        ? 'border-l-primary-600 bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                        : 'border-l-transparent font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700'
                                    }`}
                                  >
                                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                    </svg>
                                    <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                                    <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                                      isSelected
                                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                                        : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                                    }`}>
                                      {count}
                                    </span>
                                  </button>
                                );
                              })}
                              {rootFiles.length > 0 && (() => {
                                const isSelected = contentView === 'files';
                                return (
                                  <button
                                    onClick={selectRootFiles}
                                    className={`flex w-full items-center gap-2.5 border-l-2 px-4 py-2.5 text-left text-sm transition-colors ${
                                      isSelected
                                        ? 'border-l-primary-600 bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                        : 'border-l-transparent font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700'
                                    }`}
                                  >
                                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                      <path d="M13 3v5a2 2 0 002 2h5" />
                                    </svg>
                                    <span className="min-w-0 flex-1 truncate">Files</span>
                                    <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                                      isSelected
                                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                                        : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                                    }`}>
                                      {rootFiles.length}
                                    </span>
                                  </button>
                                );
                              })()}
                              {materialsEntries.map((entry) => {
                                const isSelected = contentView === entry.view;
                                return (
                                  <button
                                    key={entry.view}
                                    onClick={() => selectMaterials(entry.view)}
                                    className={`flex w-full items-center justify-between gap-2 border-l-2 px-4 py-2.5 text-left text-sm transition-colors ${
                                      isSelected
                                        ? 'border-l-primary-600 bg-primary-50 font-bold text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                                        : 'border-l-transparent font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-secondary-700'
                                    }`}
                                  >
                                    <span className="truncate">{entry.label}</span>
                                    <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                                      isSelected
                                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                                        : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                                    }`}>
                                      {entry.count}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </aside>

                    {/* Right pane */}
                    <div className="min-w-0 flex-1">
                      {/* --- Selected lecture detail --- */}
                      {contentView === 'lecture' && selectedLecture && (
                        <div className="space-y-8">
                          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                            {/* Thumbnail (capped to roughly half the pane so it does not dominate) */}
                            <div
                              onClick={() => navigate(`/student/courses/${course.course_id}/lectures/${selectedLecture.lecture_id}`)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/student/courses/${course.course_id}/lectures/${selectedLecture.lecture_id}`); } }}
                              className="group relative mx-auto mt-4 aspect-video w-full max-w-2xl cursor-pointer overflow-hidden rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 px-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:from-secondary-700 dark:to-secondary-800">
                              {/* Centered play button */}
                              <span className="absolute inset-0 z-10 flex items-center justify-center">
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-primary-700 shadow-large transition-transform duration-200 group-hover:scale-110">
                                  <svg className="ml-1 h-7 w-7" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                </span>
                              </span>
                              {selectedLecture.thumbnail_path ? (
                                <img
                                  src={`${apiUrl}${selectedLecture.thumbnail_path}`}
                                  alt={selectedLecture.title}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                  <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                              {selectedLecture.duration > 0 && (
                                <span className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2.5 py-0.5 text-xs font-bold text-white">
                                  {formatDuration(selectedLecture.duration)}
                                </span>
                              )}
                              {/* Progress bar when this is the last-watched lecture */}
                              {lastWatched?.lecture_id === selectedLecture.lecture_id && (
                                <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/20">
                                  <div
                                    className="h-full bg-primary-500"
                                    style={{ width: `${Math.min(100, Math.max(2, lastWatched.percent))}%` }}
                                  />
                                </div>
                              )}
                              {/* AI processing status pill */}
                              {(() => {
                                const hasAI = selectedLecture.summary || (selectedLecture.topics && selectedLecture.topics.length > 0);
                                const uploadedRecently = new Date(selectedLecture.uploaded_at).getTime() > Date.now() - 30 * 60 * 1000; // 30 min
                                if (hasAI) {
                                  return (
                                    <span className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 shadow-sm dark:bg-emerald-900/25 dark:text-emerald-300">
                                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                      AI ready
                                    </span>
                                  );
                                } else if (uploadedRecently) {
                                  return (
                                    <span className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 shadow-sm dark:bg-amber-900/25 dark:text-amber-300">
                                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
                                      Processing
                                    </span>
                                  );
                                } else {
                                  return (
                                    <span className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 shadow-sm dark:bg-secondary-700 dark:text-gray-400">
                                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                                      Pending
                                    </span>
                                  );
                                }
                              })()}
                            </div>

                            {/* Details */}
                            <div className="flex flex-col gap-3 p-6">
                              <p className="text-xs font-bold text-gray-500 dark:text-gray-400">
                                Lecture {selectedIdx + 1}
                                {selectedLecture.duration ? ` · ${formatDuration(selectedLecture.duration)}` : ''}
                                {` · Added ${new Date(selectedLecture.uploaded_at).toLocaleDateString()}`}
                              </p>
                              <h2 className="text-xl font-extrabold leading-snug tracking-tight text-gray-900 dark:text-white sm:text-2xl">
                                {selectedLecture.title}
                              </h2>
                              {selectedLecture.description && (
                                <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                                  {selectedLecture.description}
                                </p>
                              )}
                              {selectedLecture.summary && (
                                <p className="text-sm italic leading-relaxed text-gray-500 dark:text-gray-400">
                                  {selectedLecture.summary}
                                </p>
                              )}
                              {selectedLecture.topics && selectedLecture.topics.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {selectedLecture.topics.map((t) => (
                                    <span
                                      key={t}
                                      className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleExtractTopics(selectedLecture.lecture_id)}
                                  className="w-fit text-xs font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                                >
                                  Extract topics for this lecture
                                </button>
                              )}
                              <div className="mt-1">
                                <button
                                  onClick={() => navigate(`/student/courses/${course.course_id}/lectures/${selectedLecture.lecture_id}`)}
                                  className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                                >
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                  {lastWatched?.lecture_id === selectedLecture.lecture_id ? 'Resume lecture' : 'Watch lecture'}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Recommended videos tied to this lecture */}
                          {lectureRecommendedVideos.length > 0 && (
                            <div className="space-y-3">
                              <h3 className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
                                Recommended for this lecture
                              </h3>
                              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                {lectureRecommendedVideos.map((video) => renderVideoCard(video))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* --- Slides and presentations --- */}
                      {contentView === 'slides' && (
                        <>
                          {slidesLoading ? (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                              {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="h-64 animate-pulse rounded-2xl bg-gray-200 dark:bg-secondary-800" />
                              ))}
                            </div>
                          ) : slides.length === 0 ? (
                            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                              <Mascot name="folder" chip="#93c5fd" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
                              <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No presentations yet</h3>
                              <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">The instructor hasn't uploaded any presentations for this course yet</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                              {slides.map((slide) => (
                                <div
                                  key={slide.slide_id}
                                  className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
                                >
                                  {/* Icon tile + file type */}
                                  <div className="flex items-start justify-between">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        <path d="M13 3v5a2 2 0 002 2h5" />
                                        <path d="M9 13h6M9 17h4" />
                                      </svg>
                                    </span>
                                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold uppercase text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                      {slide.file_name.split('.').pop()}
                                    </span>
                                  </div>

                                  {/* Title + subtitle */}
                                  <h3 className="mt-4 line-clamp-2 text-base font-bold leading-snug text-gray-900 dark:text-white">
                                    {slide.title}
                                  </h3>
                                  <p className="mt-1 line-clamp-1 text-sm text-gray-500 dark:text-gray-400">
                                    {slide.file_name}
                                  </p>

                                  {/* Meta row */}
                                  <div className="mt-3 flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                                    <span className="inline-flex items-center gap-1.5">
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                      {formatFileSize(slide.file_size)}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      {new Date(slide.uploaded_at).toLocaleDateString()}
                                    </span>
                                  </div>

                                  {/* Primary CTA */}
                                  <div className="mt-4 flex items-center justify-end border-t border-gray-100 pt-4 dark:border-secondary-700">
                                    <a
                                      href={`${apiUrl}${slide.file_path}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                      Download
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}

                      {/* --- Recommended videos (all) --- */}
                      {contentView === 'recommended' && (
                        <>
                          {recommendedLoading ? (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                              {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="h-64 animate-pulse rounded-2xl bg-gray-200 dark:bg-secondary-800" />
                              ))}
                            </div>
                          ) : recommendedVideos.length === 0 ? (
                            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                              <Mascot name="bulb" chip="#fb923c" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
                              <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No recommended videos yet</h3>
                              <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">The instructor hasn't added any recommended videos yet</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                              {recommendedVideos.map((video) => renderVideoCard(video))}
                            </div>
                          )}
                        </>
                      )}

                      {/* --- Content folder detail --- */}
                      {contentView === 'folder' && selectedFolder && (
                        renderFilesPane(selectedFolder.name, filesInFolder(selectedFolder.folder_id))
                      )}

                      {/* --- Root-level files --- */}
                      {contentView === 'files' && renderFilesPane('Files', rootFiles)}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* === QUIZZES TAB === */}
          {activeTab === 'quizzes' && (
            <>
              {quizzesLoading ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-44 animate-pulse rounded-2xl bg-gray-200 dark:bg-secondary-800" />
                  ))}
                </div>
              ) : quizView === 'list' ? (
                /* Quiz List View */
                quizzes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                    <Mascot name="book" chip="#fbbf24" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
                    <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No quizzes available</h3>
                    <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">The instructor hasn't created any quizzes yet</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Up next in this course: server-filtered, so completed work never counts down */}
                    {courseUpNext && (
                      <div className="max-w-md">
                        <UpNextCard
                          item={courseUpNext}
                          onOpen={
                            courseUpNext.status === 'open' && courseUpNext.item_type !== 'assignment'
                              ? () => startQuiz(courseUpNext.quiz_id)
                              : undefined
                          }
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {quizzes.map((quiz) => {
                      const schedule = getQuizScheduleState(quiz);
                      const msToDue = quiz.due_at ? new Date(quiz.due_at).getTime() - Date.now() : null;
                      return (
                        <div
                          key={quiz.quiz_id}
                          className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
                        >
                          <div className="flex items-start gap-4">
                            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                <path d="M9 14l2 2 4-4" />
                              </svg>
                            </span>
                            <div className="min-w-0 flex-1">
                              <h3 className="line-clamp-2 text-base font-bold leading-snug text-gray-900 dark:text-white">{quiz.title}</h3>
                              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Knowledge check</p>
                            </div>
                          </div>

                          {/* Quiet meta row */}
                          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                            <span className="inline-flex items-center gap-1.5">
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              {quiz.num_questions} question{quiz.num_questions !== 1 ? 's' : ''}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Created {new Date(quiz.created_at).toLocaleDateString()}
                            </span>
                            {schedule === 'scheduled' && quiz.available_from && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                                Opens {formatShortDateTime(quiz.available_from)}
                              </span>
                            )}
                            {schedule === 'open' && quiz.due_at && msToDue !== null && (
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                msToDue < 24 * 3_600_000
                                  ? 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300'
                                  : msToDue < 48 * 3_600_000
                                    ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                              }`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${
                                  msToDue < 24 * 3_600_000 ? 'bg-red-500' : msToDue < 48 * 3_600_000 ? 'bg-amber-500' : 'bg-gray-400'
                                }`} />
                                Due {formatShortDateTime(quiz.due_at)}
                              </span>
                            )}
                            {schedule === 'closed' && quiz.due_at && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                Closed {formatShortDate(quiz.due_at)}
                              </span>
                            )}
                            {(quiz.max_attempts ?? 0) > 0 ? (
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                (quiz.my_attempts ?? 0) >= (quiz.max_attempts ?? 0)
                                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                                  : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                              }`}>
                                {(quiz.my_attempts ?? 0) >= (quiz.max_attempts ?? 0) && (
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                                )}
                                Attempts {quiz.my_attempts ?? 0}/{quiz.max_attempts}
                              </span>
                            ) : quiz.my_attempted ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                                Attempted
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                                Not attempted
                              </span>
                            )}
                          </div>

                          {/* Primary CTA */}
                          <div className="mt-4 flex items-center justify-end border-t border-gray-100 pt-4 dark:border-secondary-700">
                            {schedule === 'scheduled' ? (
                              <button
                                disabled
                                className="cursor-not-allowed rounded-full border-2 border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-500 dark:border-secondary-700 dark:text-gray-400"
                              >
                                Not open yet
                              </button>
                            ) : schedule === 'closed' ? (
                              <button
                                disabled
                                className="cursor-not-allowed rounded-full border-2 border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-500 dark:border-secondary-700 dark:text-gray-400"
                              >
                                Closed
                              </button>
                            ) : (quiz.max_attempts ?? 0) > 0 && (quiz.my_attempts ?? 0) >= (quiz.max_attempts ?? 0) ? (
                              <button
                                disabled
                                className="cursor-not-allowed rounded-full border-2 border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-500 dark:border-secondary-700 dark:text-gray-400"
                              >
                                No attempts left
                              </button>
                            ) : (
                              <button
                                onClick={() => startQuiz(quiz.quiz_id)}
                                disabled={quizLoadingId === quiz.quiz_id}
                                className="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50"
                              >
                                {quizLoadingId === quiz.quiz_id ? 'Loading...' : 'Take quiz'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                )
              ) : quizView === 'taking' && activeQuiz ? (
                /* Quiz Taking View: one question at a time */
                (() => {
                  const questions = activeQuiz.questions;
                  const total = questions.length;
                  const safeIndex = Math.min(currentQuestionIndex, Math.max(0, total - 1));
                  const q = questions[safeIndex];
                  const hasAnswer = (id: string) => (quizAnswers[id] || '').trim().length > 0;
                  const answeredCount = questions.filter((qq) => hasAnswer(qq.question_id)).length;
                  const isLast = safeIndex === total - 1;
                  const bloomLabel = typeof (q as any).bloom_level_label === 'string' ? (q as any).bloom_level_label : undefined;

                  const exitQuiz = () => {
                    if (answeredCount > 0 && !window.confirm('Exit this quiz? Your answers will not be saved.')) return;
                    setQuizView('list');
                    setActiveQuiz(null);
                  };

                  const confirmAndSubmit = () => {
                    const unanswered = total - answeredCount;
                    if (
                      unanswered > 0 &&
                      !window.confirm(`You have ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}. Submit anyway?`)
                    ) {
                      return;
                    }
                    handleSubmitQuiz();
                  };

                  return (
                    <div className="space-y-6">
                      {/* Sticky top bar: exit, progress, position */}
                      <div className="sticky top-0 z-20 bg-white pb-1 pt-2 dark:bg-secondary-900">
                        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:gap-4 sm:px-5">
                          <button
                            onClick={exitQuiz}
                            className="inline-flex flex-shrink-0 items-center gap-1.5 text-sm font-bold text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="hidden sm:inline">Exit quiz</span>
                          </button>
                          <div className="h-3.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-secondary-700">
                            <div
                              className="h-full rounded-full bg-primary-500 transition-all duration-300"
                              style={{ width: `${total > 0 ? (answeredCount / total) * 100 : 0}%` }}
                            />
                          </div>
                          <p className="flex-shrink-0 text-sm font-bold text-gray-500 dark:text-gray-400">
                            Question {safeIndex + 1} of {total}
                          </p>
                        </div>
                      </div>

                      {q && (
                        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-8">
                          {/* Badges */}
                          {(bloomLabel || q.question_type === 'open_ended') && (
                            <div className="mb-4 flex flex-wrap items-center gap-2">
                              <BloomBadge label={bloomLabel} />
                              {q.question_type === 'open_ended' && (
                                <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                  Open-ended
                                </span>
                              )}
                            </div>
                          )}

                          {/* Question text */}
                          <h2 className="text-xl font-extrabold leading-snug tracking-tight text-gray-900 dark:text-white sm:text-2xl">
                            {q.question_text}
                          </h2>

                          {q.question_type === 'open_ended' ? (
                            /* Open-ended question: textarea */
                            <div className="mt-6">
                              <textarea
                                rows={6}
                                value={quizAnswers[q.question_id] || ''}
                                onChange={(e) => setQuizAnswers({ ...quizAnswers, [q.question_id]: e.target.value })}
                                placeholder="Type your answer here..."
                                className="w-full resize-y rounded-2xl border-2 border-gray-200 bg-white p-3.5 text-sm text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
                              />
                              <div className="mt-1.5 flex items-center justify-between gap-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {(quizAnswers[q.question_id] || '').length} characters
                                </p>
                                {q.rubric?.dimensions && (
                                  <p className="text-right text-xs text-gray-500 dark:text-gray-400">
                                    Assessed on: {q.rubric.dimensions.map(d => d.name).join(', ')}
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            /* MCQ question: big selectable option cards */
                            <div className="mt-6 space-y-3">
                              {q.options?.map((opt) => {
                                const picked = quizAnswers[q.question_id] === opt.label;
                                return (
                                  <label
                                    key={opt.label}
                                    className={`flex cursor-pointer items-center gap-4 rounded-2xl border-2 px-5 py-4 text-base font-medium transition-all duration-200 ${
                                      picked
                                        ? 'border-primary-500 bg-primary-50 text-gray-900 dark:border-primary-500 dark:bg-primary-900/20 dark:text-white'
                                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name={q.question_id}
                                      value={opt.label}
                                      checked={picked}
                                      onChange={() => setQuizAnswers({ ...quizAnswers, [q.question_id]: opt.label })}
                                      className="sr-only"
                                    />
                                    <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-extrabold transition-colors ${
                                      picked
                                        ? 'bg-primary-600 text-gray-900'
                                        : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                                    }`}>
                                      {opt.label}
                                    </span>
                                    <span>{opt.text}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}

                          {/* Bottom controls */}
                          <div className="mt-8 flex items-center justify-between gap-3 border-t border-gray-100 pt-6 dark:border-secondary-700">
                            {safeIndex > 0 ? (
                              <button
                                onClick={() => setCurrentQuestionIndex(safeIndex - 1)}
                                className="rounded-full border-2 border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-700"
                              >
                                Previous
                              </button>
                            ) : (
                              <span />
                            )}
                            {isLast ? (
                              <button
                                onClick={confirmAndSubmit}
                                disabled={quizSubmitting}
                                className="rounded-full bg-primary-600 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50"
                              >
                                {quizSubmitting ? 'Submitting...' : 'Submit quiz'}
                              </button>
                            ) : (
                              <button
                                onClick={() => setCurrentQuestionIndex(safeIndex + 1)}
                                className="rounded-full bg-primary-600 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                              >
                                Next
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Question dot navigator */}
                      <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-2">
                        {questions.map((qq, i) => {
                          const answered = hasAnswer(qq.question_id);
                          const current = i === safeIndex;
                          return (
                            <button
                              key={qq.question_id}
                              onClick={() => setCurrentQuestionIndex(i)}
                              aria-label={`Go to question ${i + 1}`}
                              aria-current={current ? 'step' : undefined}
                              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-extrabold transition-all ${
                                answered
                                  ? 'bg-primary-600 text-gray-900'
                                  : 'border-2 border-gray-200 bg-white text-gray-500 dark:border-secondary-600 dark:bg-secondary-800 dark:text-gray-400'
                              } ${current ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-secondary-900' : ''}`}
                            >
                              {i + 1}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()
              ) : quizView === 'submitted' ? (
                /* Quiz Submitted Confirmation: grades are released later by the instructor */
                <div className="mx-auto max-w-2xl">
                  <div className="flex flex-col items-center rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800 sm:p-10">
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/25 dark:text-emerald-300">
                      <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <h2 className="mt-5 text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">Quiz submitted</h2>
                    <p className="mt-2 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                      Your instructor will review your answers. Your grade and feedback will appear in the Grades tab once released.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                      <button
                        onClick={() => { setQuizView('list'); setActiveQuiz(null); }}
                        className="rounded-full bg-primary-600 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                      >
                        Back to quizzes
                      </button>
                      <button
                        onClick={() => { setQuizView('list'); setActiveQuiz(null); setActiveTab('grades'); }}
                        className="rounded-full border-2 border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-700"
                      >
                        Go to Grades
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* === ASSIGNMENTS TAB === */}
          {activeTab === 'assignments' && (
            <>
              {assignmentsLoading ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-44 animate-pulse rounded-2xl bg-gray-200 dark:bg-secondary-800" />
                  ))}
                </div>
              ) : assignmentView === 'list' ? (
                /* Assignment List View */
                assignments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                    <Mascot name="folder" chip="#93c5fd" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
                    <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No assignments yet</h3>
                    <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">The instructor hasn't posted any assignments for this course yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {assignments.map((assignment) => {
                      const status = getSubmissionStatus(mySubmissions[assignment.assignment_id]);
                      return (
                        <div
                          key={assignment.assignment_id}
                          className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
                        >
                          <div className="flex items-start gap-4">
                            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                            </span>
                            <div className="min-w-0 flex-1">
                              <h3 className="line-clamp-2 text-base font-bold leading-snug text-gray-900 dark:text-white">{assignment.title}</h3>
                              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Assignment</p>
                            </div>
                          </div>

                          {/* Quiet meta row + status pill */}
                          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                            {assignment.due_at && (
                              <span className="inline-flex items-center gap-1.5">
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Due {formatShortDateTime(assignment.due_at)}
                              </span>
                            )}
                            {assignment.attachments.length > 0 && (
                              <span className="inline-flex items-center gap-1.5">
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                {assignment.attachments.length} attachment{assignment.attachments.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {status === 'none' && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                                <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                                Not submitted
                              </span>
                            )}
                            {status === 'submitted' && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                Submitted
                              </span>
                            )}
                            {status === 'late' && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                Submitted late
                              </span>
                            )}
                            {status === 'graded' && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                                Graded
                              </span>
                            )}
                          </div>

                          {/* Primary CTA */}
                          <div className="mt-4 flex items-center justify-end border-t border-gray-100 pt-4 dark:border-secondary-700">
                            <button
                              onClick={() => openAssignment(assignment.assignment_id)}
                              className="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                            >
                              Open
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (() => {
                /* Assignment Detail View */
                const assignment = assignments.find((a) => a.assignment_id === activeAssignmentId);
                if (!assignment) return null;
                const sub = mySubmissions[assignment.assignment_id] || null;
                const now = Date.now();
                const msToDue = assignment.due_at ? new Date(assignment.due_at).getTime() - now : null;
                const pastDue = msToDue !== null && msToDue < 0;
                const opensAt = assignment.available_from ? new Date(assignment.available_from).getTime() : null;

                const renderSubmitForm = (isResubmit: boolean) => (
                  <div className="mt-4 space-y-4">
                    {isResubmit && (
                      <div className="rounded-2xl bg-amber-50 p-3.5 text-sm font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                        Resubmitting replaces your previous work and resets any grading on it.
                      </div>
                    )}
                    {pastDue && (
                      <div className="rounded-2xl bg-red-50 p-3.5 text-sm font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                        The due date has passed. This will be marked late.
                      </div>
                    )}
                    <div>
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-700">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Choose files
                        <input
                          type="file"
                          multiple
                          className="sr-only"
                          onChange={(e) => setSubmitFiles(Array.from(e.target.files || []))}
                        />
                      </label>
                      {submitFiles.length > 0 ? (
                        <ul className="mt-3 space-y-1.5">
                          {submitFiles.map((f, i) => (
                            <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <svg className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <span className="truncate font-semibold">{f.name}</span>
                              <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">{formatFileSize(f.size)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Any file types, multiple files allowed</p>
                      )}
                    </div>
                    <textarea
                      rows={3}
                      value={submitComment}
                      onChange={(e) => setSubmitComment(e.target.value)}
                      placeholder="Add a comment for your instructor (optional)"
                      className="w-full resize-y rounded-2xl border-2 border-gray-200 bg-white p-3.5 text-sm text-gray-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white"
                    />
                    {uploadProgress !== null && (
                      <div>
                        <div className="h-3.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-secondary-700">
                          <div
                            className="h-full rounded-full bg-primary-500 transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">Uploading {uploadProgress}%</p>
                      </div>
                    )}
                    <button
                      onClick={() => handleSubmitAssignment(assignment.assignment_id)}
                      disabled={submitFiles.length === 0 || assignmentSubmitting}
                      className="rounded-full bg-primary-600 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50"
                    >
                      {assignmentSubmitting ? 'Submitting...' : 'Submit assignment'}
                    </button>
                  </div>
                );

                return (
                  <div className="space-y-6">
                    {/* Back pill */}
                    <button
                      onClick={() => setAssignmentView('list')}
                      className="inline-flex items-center gap-1.5 rounded-full border-2 border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-800"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 19l-7-7 7-7" />
                      </svg>
                      Back to assignments
                    </button>

                    {/* Assignment details */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <h2 className="text-xl font-extrabold leading-snug tracking-tight text-gray-900 dark:text-white sm:text-2xl">
                        {assignment.title}
                      </h2>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {opensAt !== null && opensAt > now && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                            Opens {formatShortDateTime(assignment.available_from!)}
                          </span>
                        )}
                        {assignment.due_at && msToDue !== null && (
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            pastDue
                              ? 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300'
                              : msToDue < 48 * 3_600_000
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'
                                : 'bg-gray-100 text-gray-500 dark:bg-secondary-700 dark:text-gray-400'
                          }`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${
                              pastDue ? 'bg-red-500' : msToDue < 48 * 3_600_000 ? 'bg-amber-500' : 'bg-gray-400'
                            }`} />
                            Due {formatShortDateTime(assignment.due_at)}
                          </span>
                        )}
                      </div>
                      {assignment.instructions ? (
                        <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                          {assignment.instructions}
                        </p>
                      ) : (
                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">No instructions provided</p>
                      )}

                      {/* Attachments */}
                      {assignment.attachments.length > 0 && (
                        <div className="mt-5 border-t border-gray-100 pt-5 dark:border-secondary-700">
                          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Attachments</p>
                          <div className="mt-3 space-y-3">
                            {assignment.attachments.map((att) => (
                              <div key={att.file_id} className="flex items-center gap-3">
                                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                                  {FILE_GLYPHS[getFileGlyphGroup(att.file_name)]}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{att.title || att.file_name}</p>
                                  <p className="mt-0.5 truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {att.file_name} &middot; {formatFileSize(att.file_size)}
                                  </p>
                                </div>
                                <a
                                  href={`${apiUrl}${att.file_path}`}
                                  download={att.file_name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  Download
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* My submission */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <h3 className="text-base font-extrabold tracking-tight text-gray-900 dark:text-white">My submission</h3>
                      {!sub ? (
                        renderSubmitForm(false)
                      ) : (
                        <div className="mt-3 space-y-4">
                          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Submitted {formatShortDateTime(sub.submitted_at)}
                            {sub.submitted_late && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/25 dark:text-red-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                Late
                              </span>
                            )}
                          </div>

                          {/* Submitted files */}
                          {sub.files.length > 0 && (
                            <div className="space-y-2">
                              {sub.files.map((f) => (
                                <a
                                  key={f.file_id}
                                  href={`${apiUrl}${f.file_path}`}
                                  download={f.file_name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                                >
                                  <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  <span className="truncate">{f.file_name}</span>
                                  <span className="flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">{formatFileSize(f.file_size)}</span>
                                </a>
                              ))}
                            </div>
                          )}

                          {/* Comment */}
                          {sub.comment && sub.comment.trim() && (
                            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3.5 dark:border-secondary-600 dark:bg-secondary-700">
                              <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Your comment</p>
                              <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{sub.comment}</p>
                            </div>
                          )}

                          {/* Released grade + feedback */}
                          {sub.released && (
                            <div className="rounded-2xl bg-emerald-50 p-5 dark:bg-emerald-900/25">
                              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Grade</p>
                              <p className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                {sub.grade_percentage !== null && sub.grade_percentage !== undefined
                                  ? `${Math.round(sub.grade_percentage)}%`
                                  : 'Released'}
                              </p>
                              {sub.feedback && sub.feedback.trim() && (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Feedback from your instructor</p>
                                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{sub.feedback}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Resubmit */}
                          {!showResubmitForm ? (
                            <button
                              onClick={() => { setShowResubmitForm(true); setSubmitFiles([]); setSubmitComment(''); }}
                              className="rounded-full border-2 border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-700"
                            >
                              Resubmit
                            </button>
                          ) : (
                            renderSubmitForm(true)
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* === GRADES TAB === */}
          {activeTab === 'grades' && (
            <>
              {gradesLoading ? (
                <div className="space-y-4">
                  <div className="h-40 animate-pulse rounded-2xl bg-gray-200 dark:bg-secondary-800" />
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-20 animate-pulse rounded-2xl bg-gray-200 dark:bg-secondary-800" />
                  ))}
                </div>
              ) : !grades || grades.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                  <Mascot name="book" chip="#fbbf24" className="h-20 w-20 shadow-medium animate-float-slow" blinkDelay={0.4} />
                  <h3 className="mt-5 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No graded work yet</h3>
                  <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">Grades will show up here once the instructor publishes quizzes and assignments</p>
                </div>
              ) : (() => {
                const items = grades.items as GradeItem[];
                const pendingCount = items.filter((it) => it.pending).length;
                const releasedCount = items.filter(
                  (it) => !it.pending && it.best_percentage !== null && it.best_percentage !== undefined
                ).length;
                // Pending items are completed work awaiting a released grade
                const completedCount = releasedCount + pendingCount;
                const average = grades.average ?? null;
                const averageBand = average !== null ? getGradeBand(average) : null;

                const renderGradeRow = (item: GradeItem) => {
                  const pct = item.best_percentage ?? null;
                  const band = pct !== null ? getGradeBand(pct) : null;
                  const isAssignment = item.item_type === 'assignment';
                  const hasFeedback = !item.pending && pct !== null && !!(item.feedback && item.feedback.trim());
                  const feedbackOpen = !!expandedFeedback[item.quiz_id];
                  return (
                    <div
                      key={item.quiz_id}
                      className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800"
                    >
                      <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                        {isAssignment ? (
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        ) : (
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            <path d="M9 14l2 2 4-4" />
                          </svg>
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-sm font-bold text-gray-900 dark:text-white sm:text-base">{item.title}</h3>
                          {isAssignment && (
                            <span className="flex-shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                              Assignment
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                          {!isAssignment && <span>{item.num_questions} question{item.num_questions !== 1 ? 's' : ''}</span>}
                          {item.due_at && <span>Due {formatShortDateTime(item.due_at)}</span>}
                          <span>{item.attempts_count} attempt{item.attempts_count !== 1 ? 's' : ''}</span>
                          {item.last_attempt_at && <span>Last attempt {formatShortDate(item.last_attempt_at)}</span>}
                        </div>
                      </div>
                      {item.pending ? (
                        <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          Pending review
                        </span>
                      ) : pct !== null && band !== null ? (
                        <div className="flex flex-shrink-0 items-center gap-3">
                          <span className="text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
                            {Math.round(pct)}%
                          </span>
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${GRADE_PILL_CLASSES[band]}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${GRADE_DOT_CLASSES[band]}`} />
                            {band === 'emerald' ? 'On track' : band === 'amber' ? 'Getting there' : 'Needs work'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-shrink-0 items-center gap-3">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                            {isAssignment ? 'Not submitted' : 'Not attempted'}
                          </span>
                          <button
                            onClick={() => setActiveTab(isAssignment ? 'assignments' : 'quizzes')}
                            className="text-xs font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                          >
                            {isAssignment ? 'View assignment' : 'Take quiz'}
                          </button>
                        </div>
                      )}
                      {hasFeedback && (
                        <div className="w-full">
                          <button
                            onClick={() => setExpandedFeedback((prev) => ({ ...prev, [item.quiz_id]: !prev[item.quiz_id] }))}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                          >
                            <svg
                              className={`h-3.5 w-3.5 transition-transform duration-200 ${feedbackOpen ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
                            >
                              <path d="M19 9l-7 7-7-7" />
                            </svg>
                            Feedback from your instructor
                          </button>
                          {feedbackOpen && (
                            <div className="mt-2 rounded-2xl border border-gray-100 bg-gray-50 p-3.5 dark:border-secondary-600 dark:bg-secondary-700">
                              <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{item.feedback}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div className="space-y-6">
                    {/* Course average summary */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Course average</p>
                      {average !== null && averageBand !== null ? (
                        <p className="mt-2 text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-6xl">
                          {Math.round(average)}%
                        </p>
                      ) : (
                        <p className="mt-2 text-2xl font-extrabold tracking-tight text-gray-400 dark:text-gray-500 sm:text-3xl">
                          No grades yet
                        </p>
                      )}
                      <div className="mt-5 h-3.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-secondary-700">
                        {average !== null && averageBand !== null && (
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${GRADE_BAR_CLASSES[averageBand]}`}
                            style={{ width: `${Math.min(100, Math.max(0, average))}%` }}
                          />
                        )}
                      </div>
                      <p className="mt-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                        {completedCount} of {items.length} completed
                        {pendingCount > 0 && ` · ${releasedCount} graded · ${pendingCount} awaiting review`}
                      </p>
                    </div>

                    {/* Per-quiz grade rows */}
                    <div className="space-y-4">
                      {items.map((item) => renderGradeRow(item))}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

      <FilePreviewModal
        file={previewFile ? {
          title: previewFile.title,
          file_name: previewFile.file_name,
          kind: previewFile.kind === 'link' ? 'link' : 'file',
          url: previewFile.kind === 'link' ? (previewFile.url || '') : `${apiUrl}${previewFile.file_path}`,
        } : null}
        onClose={() => setPreviewFile(null)}
      />
    </Layout>
  );
};

export default StudentCoursePage;
